import { BybSection } from "@Components/common/byb-ds";
import { BlurredGraphContainer, BlurredGraphOverlay, PerformanceMessage } from "@Components/game/playlists/playlist/styled-components";
import RadarGraph from "@Containers/shared/cards/radar-graph";
import MetricEventTypes from "@Enums/MetricEventTypes";
import useI18n from "@i18n";
import { Paper, useTheme } from "@mui/material";

interface IPlaylistPerformance {
  loading: boolean;
  currentStats?: {
    awareness_score: number;
    scan_rate: number;
    scan_timing: number;
    total_score: number;
    critical_scan_score: number;
    byb_iq: number;
  } | null;
  previousStats?: {
    awareness_score: number;
    scan_rate: number;
    scan_timing: number;
    total_score: number;
    critical_scan_score: number;
    byb_iq: number;
  } | null;
}

const PlaylistPerformance = ({ loading, currentStats, previousStats }: IPlaylistPerformance) => {
  const theme = useTheme();
  const { i18n } = useI18n();

  const hasNoData = !currentStats && !previousStats;

  // Normalize values to 0-1 range for the radar chart
  const normalizeValue = (value: number) => Math.min(Math.max(value / 100, 0), 1);

  // Create the radar data based on the available stats
  const radarData = [
    {
      name: MetricEventTypes.AWARENESS,
      current: currentStats ? normalizeValue(currentStats.awareness_score) : 0,
      previous: previousStats ? normalizeValue(previousStats.awareness_score) : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.SCAN_FREQUENCY,
      current: currentStats ? normalizeValue(currentStats.scan_rate * 100) : 0,
      previous: previousStats ? normalizeValue(previousStats.scan_rate * 100) : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.SCAN_TIMING,
      current: currentStats ? normalizeValue(currentStats.scan_timing) : 0,
      previous: previousStats ? normalizeValue(previousStats.scan_timing) : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.DECISION,
      current: currentStats ? normalizeValue(currentStats.total_score) : 0,
      previous: previousStats ? normalizeValue(previousStats.total_score) : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.CRITICAL_SCAN,
      current: currentStats ? normalizeValue(currentStats.critical_scan_score) : 0,
      previous: previousStats ? normalizeValue(previousStats.critical_scan_score) : 0,
      fullMark: 100,
      domain: [0, 100],
    },
  ];

  const getRadarGraphIndicators = () => [
    ...(currentStats
      ? [
          {
            name: "Recent",
            color: theme.palette.dsPrimary.v300,
          },
        ]
      : [
          {
            name: "Not available",
            color: theme.palette.dsWarning.v100,
          },
        ]),
    ...(previousStats
      ? [
          {
            name: "Previous",
            color: theme.palette.dsDestructive.v200,
          },
        ]
      : []),
  ];

  return (
    <Paper data-shape="squircle" sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <BybSection
        title={i18n("playlist.performance.header")}
        tight
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          backgroundColor: theme.palette.dsSurface.v100,
          paddingBottom: theme.spacing(2),
        }}
      >
        {hasNoData ? (
          <BlurredGraphContainer>
            <BlurredGraphOverlay>
              <RadarGraph
                indicators={[
                  {
                    name: "Recent",
                    color: theme.palette.dsWarning.v100,
                  },
                ]}
                data={radarData}
                height={300}
                currentScore={0}
                previousScore={0}
                loading={false}
              />
            </BlurredGraphOverlay>
            <PerformanceMessage variant="textLgItalic" color="dsBase.v200">
              {i18n("playlist.best.result.no.data")}
            </PerformanceMessage>
          </BlurredGraphContainer>
        ) : (
          <RadarGraph
            indicators={getRadarGraphIndicators()}
            data={radarData}
            height={300}
            currentScore={currentStats?.total_score || 0}
            previousScore={previousStats?.total_score || 0}
            loading={loading}
          />
        )}
      </BybSection>
    </Paper>
  );
};

export default PlaylistPerformance;
